import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useState } from "react";
import {
  HeartPulse,
  Heart,
  Sparkles,
  Wind,
  Sprout,
  Flower,
  Hand,
  BookOpen,
  Moon,
  Music,
  Activity as Bilateral,
  type LucideIcon,
} from "lucide-react";
import { NeedsCheck } from "@/components/NeedsCheck";
import { DailyCheckInForm } from "@/components/check-in/DailyCheckInForm";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselSwipeHint,
} from "@/components/ui/carousel";

import anxiousArt from "@/assets/feelings/feel-anxious.webp.asset.json";
import disconnectedArt from "@/assets/feelings/feel-disconnected.webp.asset.json";
import sadArt from "@/assets/feelings/feel-sad.webp.asset.json";
import triggeredArt from "@/assets/feelings/feel-triggered.webp.asset.json";
import connectionArt from "@/assets/feelings/feel-connection.webp.asset.json";
import understandArt from "@/assets/feelings/feel-understand.webp.asset.json";

export const Route = createFileRoute("/check-in")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Daily Check-In — Recovery Trauma™" },
      {
        name: "description",
        content:
          "How do you feel today? Get gentle, body-based exercises matched to your emotional state.",
      },
      { property: "og:title", content: "Daily Check-In — Recovery Trauma™" },
      { property: "og:description", content: "How do you feel today? Get gentle, body-based exercises matched to your emotional state." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/check-in" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/check-in" }],
  }),
  component: withMemberGate(CheckInPage, "Daily Check-in"),
});

type ToolRoute =
  | "/breathing"
  | "/grounding"
  | "/somatic"
  | "/tapping"
  | "/sounds"
  | "/journal"
  | "/meditations"
  | "/affirmations"
  | "/bilateral"
  | "/sleep";

type Tool = { label: string; short: string; to: ToolRoute; icon: LucideIcon };

type Feeling = {
  id: string;
  /** Soft card surface + accent, always paired with the emoji and title. */
  tint: string;
  accent: string;
  chip: string;
  emoji: string;
  title: string;
  description: string;
  art: { url: string };
  artAlt: string;
  tools: Tool[];
};

const FEELINGS: Feeling[] = [
  {
    id: "anxious",
    tint: "border-amber-500/30 bg-gradient-to-br from-amber-500/12 via-card to-card",
    accent: "text-amber-600 dark:text-amber-300",
    chip: "border-amber-500/30 bg-amber-500/10",
    emoji: "💛",
    title: "I Feel Anxious or Overwhelmed",
    description: "Calm your nervous system with gentle, body-based practices.",
    art: anxiousArt,
    artAlt: "Soft cartoon of a person breathing slowly with calm clouds",
    tools: [
      { label: "Breathing", short: "Breathing", to: "/breathing", icon: Wind },
      { label: "Grounding", short: "Grounding", to: "/grounding", icon: Sprout },
      { label: "Somatic", short: "Somatic", to: "/somatic", icon: Flower },
      { label: "EFT Tapping", short: "EFT Tapping", to: "/tapping", icon: Hand },
      { label: "Calming Sounds", short: "Sounds", to: "/sounds", icon: Music },
      { label: "Meditation", short: "Meditation", to: "/meditations", icon: Flower },
    ],
  },
  {
    id: "disconnected",
    tint: "border-emerald-500/30 bg-gradient-to-br from-emerald-500/12 via-card to-card",
    accent: "text-emerald-600 dark:text-emerald-300",
    chip: "border-emerald-500/30 bg-emerald-500/10",
    emoji: "🌿",
    title: "I Feel Disconnected or Numb",
    description: "Soft ways to come back into your body and the present.",
    art: disconnectedArt,
    artAlt: "Soft cartoon of a person reconnecting with a leafy tree",
    tools: [
      { label: "Somatic", short: "Somatic", to: "/somatic", icon: Flower },
      { label: "Grounding", short: "Grounding", to: "/grounding", icon: Sprout },
      { label: "Bilateral", short: "Bilateral", to: "/bilateral", icon: Bilateral },
      { label: "Calming Sounds", short: "Sounds", to: "/sounds", icon: Music },
      { label: "Breathing", short: "Breathing", to: "/breathing", icon: Wind },
      { label: "Meditation", short: "Meditation", to: "/meditations", icon: Flower },
    ],
  },
  {
    id: "sad",
    tint: "border-sky-500/30 bg-gradient-to-br from-sky-500/12 via-card to-card",
    accent: "text-sky-600 dark:text-sky-300",
    chip: "border-sky-500/30 bg-sky-500/10",
    emoji: "🌧️",
    title: "I Feel Sad or Grieving",
    description: "Soften the weight with reflection, rest, and comfort.",
    art: sadArt,
    artAlt: "Soft cartoon of a person wrapped in a knit blanket holding tea",
    tools: [
      { label: "Recovery Journal", short: "Recovery Journal", to: "/journal", icon: BookOpen },
      { label: "Affirmations", short: "Affirmations", to: "/affirmations", icon: Sparkles },
      { label: "Meditations", short: "Meditations", to: "/meditations", icon: Flower },
      { label: "Sleep & Dreams", short: "Sleep", to: "/sleep", icon: Moon },
      { label: "Calming Sounds", short: "Sounds", to: "/sounds", icon: Music },
      { label: "Breathing", short: "Breathing", to: "/breathing", icon: Wind },
    ],
  },
  {
    id: "stressed",
    tint: "border-rose-500/30 bg-gradient-to-br from-rose-500/12 via-card to-card",
    accent: "text-rose-600 dark:text-rose-300",
    chip: "border-rose-500/30 bg-rose-500/10",
    emoji: "⚡",
    title: "I Feel Stressed or Tense",
    description: "Release stored tension and bring your system back to centre.",
    art: triggeredArt,
    artAlt: "Soft cartoon of a person releasing tension gently",
    tools: [
      { label: "EFT Tapping", short: "EFT Tapping", to: "/tapping", icon: Hand },
      { label: "Breathing", short: "Breathing", to: "/breathing", icon: Wind },
      { label: "Somatic", short: "Somatic", to: "/somatic", icon: Flower },
      { label: "Sounds", short: "Sounds", to: "/sounds", icon: Music },
      { label: "Grounding", short: "Grounding", to: "/grounding", icon: Sprout },
      { label: "Bilateral", short: "Bilateral", to: "/bilateral", icon: Bilateral },
    ],
  },
  {
    id: "calm",
    tint: "border-teal-500/30 bg-gradient-to-br from-teal-500/12 via-card to-card",
    accent: "text-teal-600 dark:text-teal-300",
    chip: "border-teal-500/30 bg-teal-500/10",
    emoji: "☀️",
    title: "I Feel Calm and Grounded",
    description: "Deepen this — your nervous system is learning safety.",
    art: connectionArt,
    artAlt: "Soft cartoon of a calm, grounded person at peace",
    tools: [
      { label: "Meditations", short: "Meditations", to: "/meditations", icon: Flower },
      { label: "Recovery Journal", short: "Recovery Journal", to: "/journal", icon: BookOpen },
      { label: "Affirmations", short: "Affirmations", to: "/affirmations", icon: Sparkles },
      { label: "Sounds", short: "Sounds", to: "/sounds", icon: Music },
      { label: "Breathing", short: "Breathing", to: "/breathing", icon: Wind },
      { label: "Somatic", short: "Somatic", to: "/somatic", icon: Flower },
    ],
  },
  {
    id: "doing-well",
    tint: "border-gold/40 bg-gradient-to-br from-gold/18 via-card to-card",
    accent: "text-[var(--gold-deep)]",
    chip: "border-gold/40 bg-gold/12",
    emoji: "✨",
    title: "I Am Doing Well",
    description: "Celebrate and integrate this momentum into your recovery journey.",
    art: understandArt,
    artAlt: "Soft cartoon of a person thriving in their recovery journey",
    tools: [
      { label: "Recovery Journal", short: "Recovery Journal", to: "/journal", icon: BookOpen },
      { label: "Affirmations", short: "Affirmations", to: "/affirmations", icon: Sparkles },
      { label: "Somatic", short: "Somatic", to: "/somatic", icon: Flower },
      { label: "Meditations", short: "Meditations", to: "/meditations", icon: Flower },
      { label: "Sounds", short: "Sounds", to: "/sounds", icon: Music },
      { label: "Grounding", short: "Grounding", to: "/grounding", icon: Sprout },
    ],
  },
];

function CheckInPage() {
  const [tab, setTab] = useState<"feeling" | "state">("feeling");

  const remember = (id: string) => {
    try {
      localStorage.setItem("rt:last-feeling", id);
    } catch {}
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10">
      <header className="mb-6 text-center sm:mb-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground sm:mb-4">
          <HeartPulse className="h-3.5 w-3.5" /> Daily Check-In
        </div>
        <h1 className="font-display text-2xl tracking-tight text-foreground sm:text-4xl md:text-5xl">
          {tab === "feeling"
            ? "What's most present for you right now?"
            : "What do you need right now?"}
        </h1>
        <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground sm:text-base">
          Swipe through and follow what feels closest. There's no wrong answer — we'll meet you exactly where you are.
        </p>
      </header>

      {/* Today's check-in — mood, energy, needs, saved privately to the member */}
      <div className="mb-8">
        <DailyCheckInForm />
      </div>


      {/* Tabs */}
      <div className="mx-auto mb-8 flex w-full max-w-md rounded-full border border-border bg-card p-1">
        <button
          onClick={() => setTab("feeling")}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition ${
            tab === "feeling"
              ? "bg-[var(--gold-deep)] text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Heart className="h-3.5 w-3.5" /> Feeling
        </button>
        <button
          onClick={() => setTab("state")}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition ${
            tab === "state"
              ? "bg-[var(--gold-deep)] text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Sparkles className="h-3.5 w-3.5" /> What I Need
        </button>
      </div>

      {tab === "state" ? (
        <NeedsCheck />
      ) : (
        <Carousel
          opts={{ align: "start", loop: true }}
          className="w-full"
          aria-label="How are you feeling today"
        >
          <CarouselSwipeHint label="Swipe to explore" storageKey="checkin-feelings" />
          <CarouselContent className="-ms-5">
            {FEELINGS.map((f) => (
              <CarouselItem
                key={f.id}
                className="ps-5 basis-[88%] sm:basis-1/2 lg:basis-1/2"
              >
                <article
                  className={[
                    "group relative flex h-full flex-col rounded-[2rem] border p-6 sm:p-8 transition-all duration-300",
                    "hover:-translate-y-1 shadow-[var(--shadow-soft-lg)]",
                    f.tint,
                  ].join(" ")}
                >
                  {/* Eyebrow */}
                  <div className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${f.accent}`}>
                    <span aria-hidden>{f.emoji}</span> A gentle place to start
                  </div>

                  {/* Header row: cartoon illustration + title */}
                  <div className="mt-3 flex items-start gap-4">
                    <div
                      aria-hidden
                      className={`relative h-24 w-24 shrink-0 overflow-hidden rounded-3xl border sm:h-28 sm:w-28 ${f.chip}`}
                    >
                      <img
                        src={f.art.url}
                        alt=""
                        loading="lazy"
                        width={224}
                        height={224}
                        className="absolute inset-0 h-full w-full object-contain p-1.5"
                      />
                    </div>
                    <div className="min-w-0 pt-1">
                      <h3 className="font-display text-xl leading-tight text-foreground sm:text-2xl">
                        {f.title}
                      </h3>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="mt-3 text-sm leading-relaxed text-foreground/80 sm:text-base">
                    {f.description}
                  </p>

                  {/* Tools — direct, immediate access for members */}
                  <div className="pt-3">
                    <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${f.accent}`}>
                      Recommended tools
                    </div>
                    <ul className="mt-3 grid grid-cols-2 gap-2.5">
                      {f.tools.map((t) => {
                        const ToolIcon = t.icon;
                        return (
                          <li key={t.label}>
                            <Link
                              to={t.to}
                              onClick={() => remember(f.id)}
                              aria-label={`Open ${t.label}`}
                              className={[
                                "flex h-full min-h-[44px] w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-medium text-foreground shadow-sm transition-all sm:text-sm",
                                "hover:-translate-y-0.5 hover:shadow-md",
                                f.chip,
                              ].join(" ")}
                            >
                              <ToolIcon className={`h-4 w-4 shrink-0 ${f.accent}`} strokeWidth={2} />
                              <span className="leading-tight">{t.short}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <span className="sr-only">{f.artAlt}</span>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden sm:flex -start-3" />
          <CarouselNext className="hidden sm:flex -end-3" />
        </Carousel>
      )}
    </div>
  );
}
